import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./AdminProducts.css";
import { resolveImageUrl } from "../utils/resolveImageUrl";
import getApiBase from "../utils/apiBase";
import Seo from "../components/Seo";
import { useI18n } from "../i18n/I18nContext";
import { PRODUCT_FILTERS, getProductTags, normalizeTagValues } from "./productFilters";

const API_BASE = getApiBase();

const emptyProduct = {
  id: "",
  name: "",
  description: "",
  include: "",
  price: "",
  weight: "",
  imageUrl: "",
  videoUrl: "",
  price_id: "",
  tags: {
    brand: ["YCH-YOYO"],
    type: [],
    group: [],
  },
  colors: [],
  customAvailable: false,
  customDescription: "",
  mockupBaseImage: "",
  mockupConfig: {
    centerXRatio: 0.5,
    centerYRatio: 0.5,
    radiusRatio: 0.22,
  },
};

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const cloneProduct = (product) => JSON.parse(JSON.stringify(product));

const parseTagInput = (value) => (
  normalizeTagValues(String(value || "")
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean))
);

const formatTagInput = (values) => (
  Array.isArray(values) ? values.join(", ") : String(values || "")
);

const getEditableTagValues = (tags = {}, tagKey) => {
  const values = tags[tagKey];
  return Array.isArray(values) ? normalizeTagValues(values) : parseTagInput(values);
};

const normalizeEditableTags = (tags = {}) => {
  const normalizedTags = PRODUCT_FILTERS.reduce((result, filter) => {
    const values = Array.isArray(tags[filter.key])
      ? tags[filter.key]
      : parseTagInput(tags[filter.key]);
    result[filter.key] = normalizeTagValues(values);
    return result;
  }, {});

  if (!normalizedTags.brand.length) {
    normalizedTags.brand = ["YCH-YOYO"];
  }

  return normalizedTags;
};

const AdminProducts = () => {
  const { t } = useI18n();
  const [auth, setAuth] = useState(localStorage.getItem("adminAuth") || "");
  const [login, setLogin] = useState({ username: "", password: "" });
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(() => cloneProduct(emptyProduct));
  const [editingId, setEditingId] = useState(null);
  const [status, setStatus] = useState("");
  const [highlightForm, setHighlightForm] = useState(false);
  const formRef = useRef(null);

  const authHeaders = auth ? { Authorization: `Basic ${auth}` } : {};
  const tagSuggestions = useMemo(() => (
    PRODUCT_FILTERS.reduce((suggestions, filter) => {
      const productTags = products.flatMap((product) => getProductTags(product)[filter.key] || []);
      suggestions[filter.key] = normalizeTagValues([...filter.defaultOptions, ...productTags]);
      return suggestions;
    }, {})
  ), [products]);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/products`);
      const data = await response.json();
      setProducts(Array.isArray(data) ? data : []);
    } catch (error) {
      setStatus("Failed to load products.");
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleLogin = (event) => {
    event.preventDefault();
    const token = btoa(`${login.username}:${login.password}`);
    localStorage.setItem("adminAuth", token);
    setAuth(token);
    setStatus("Logged in.");
  };

  const handleLogout = () => {
    localStorage.removeItem("adminAuth");
    setAuth("");
    setLogin({ username: "", password: "" });
    setStatus("Logged out.");
  };

  const handleEdit = (product) => {
    setEditingId(product.id);
    setForm({
      ...cloneProduct(emptyProduct),
      ...cloneProduct(product),
      tags: getProductTags(product),
    });
    setStatus("");
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    setHighlightForm(true);
    window.setTimeout(() => setHighlightForm(false), 1200);
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(cloneProduct(emptyProduct));
  };

  const buildPayload = () => {
    const basePrice = toNumber(form.price, 0);
    return {
      ...form,
      id: form.id ? toNumber(form.id, undefined) : undefined,
      price: basePrice,
      weight: toNumber(form.weight, 0),
      tags: normalizeEditableTags(form.tags),
      colors: (form.colors || []).map((color) => ({
        name: color.name || "",
        imageUrl: color.imageUrl || "",
        price: toNumber(color.price, basePrice),
      })),
      customAvailable: Boolean(form.customAvailable),
      customDescription: form.customDescription || "",
      mockupBaseImage: form.mockupBaseImage || "",
      mockupConfig: {
        centerXRatio: toNumber(form.mockupConfig?.centerXRatio, 0.5),
        centerYRatio: toNumber(form.mockupConfig?.centerYRatio, 0.5),
        radiusRatio: toNumber(form.mockupConfig?.radiusRatio, 0.22),
      },
    };
  };

  const saveProduct = async () => {
    if (!auth) {
      setStatus("Please log in first.");
      return;
    }
    const payload = buildPayload();
    try {
      const response = await fetch(
        editingId
          ? `${API_BASE}/api/admin/products/${editingId}`
          : `${API_BASE}/api/admin/products`,
        {
          method: editingId ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders,
          },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        throw new Error("Save failed.");
      }
      await fetchProducts();
      resetForm();
      setStatus(editingId ? "Product updated." : "Product created.");
    } catch (error) {
      setStatus("Failed to save product.");
    }
  };

  const deleteProduct = async (productId) => {
    if (!auth) {
      setStatus("Please log in first.");
      return;
    }
    if (!window.confirm("Delete this product?")) {
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/api/admin/products/${productId}`, {
        method: "DELETE",
        headers: authHeaders,
      });
      if (!response.ok) {
        throw new Error("Delete failed.");
      }
      await fetchProducts();
      setStatus("Product deleted.");
    } catch (error) {
      setStatus("Failed to delete product.");
    }
  };

  const uploadImage = async (file) => {
    if (!auth) {
      setStatus("Please log in first.");
      return "";
    }
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetch(`${API_BASE}/api/admin/upload`, {
      method: "POST",
      headers: authHeaders,
      body: formData,
    });
    if (!response.ok) {
      throw new Error("Upload failed.");
    }
    const data = await response.json();
    return data.url || "";
  };

  const handleMainUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    try {
      const url = await uploadImage(file);
      if (url) {
        setForm((prev) => ({ ...prev, imageUrl: url }));
      }
    } catch (error) {
      setStatus("Failed to upload image.");
    }
  };

  const handleColorUpload = async (index, event) => {
    const file = event.target.files[0];
    if (!file) return;
    try {
      const url = await uploadImage(file);
      if (url) {
        setForm((prev) => {
          const colors = [...(prev.colors || [])];
          colors[index] = { ...colors[index], imageUrl: url };
          return { ...prev, colors };
        });
      }
    } catch (error) {
      setStatus("Failed to upload image.");
    }
  };

  const updateColor = (index, key, value) => {
    setForm((prev) => {
      const colors = [...(prev.colors || [])];
      colors[index] = { ...colors[index], [key]: value };
      return { ...prev, colors };
    });
  };

  const addColor = () => {
    setForm((prev) => ({
      ...prev,
      colors: [...(prev.colors || []), { name: "", imageUrl: "", price: prev.price || "" }],
    }));
  };

  const removeColor = (index) => {
    setForm((prev) => ({
      ...prev,
      colors: (prev.colors || []).filter((_, idx) => idx !== index),
    }));
  };

  const updateTagValues = (tagKey, values) => {
    setForm((prev) => ({
      ...prev,
      tags: {
        ...(prev.tags || {}),
        [tagKey]: values,
      },
    }));
  };

  const toggleTagValue = (tagKey, value) => {
    setForm((prev) => {
      const currentValues = getEditableTagValues(prev.tags, tagKey);
      const nextValues = currentValues.includes(value)
        ? currentValues.filter((item) => item !== value)
        : [...currentValues, value];

      return {
        ...prev,
        tags: {
          ...(prev.tags || {}),
          [tagKey]: nextValues,
        },
      };
    });
  };

  return (
    <div className="admin-products">
      <Seo
        title="Admin Products"
        description="Internal product management page."
        path="/admin"
        noindex
      />
      <header className="admin-header">
        <div>
          <h1>Product Admin</h1>
          <Link className="admin-nav-link" to="/admin/custom-requests">
            Custom Requests
          </Link>
          <Link className="admin-nav-link" to="/admin/custom-projects">
            Custom Design Projects
          </Link>
          <Link className="admin-nav-link" to="/admin/orders">
            Orders
          </Link>
          <Link className="admin-nav-link" to="/admin/community">
            Community
          </Link>
          <Link className="admin-nav-link" to="/admin/analytics">
            {t('analytics.nav')}
          </Link>
        </div>
        {auth ? (
          <button onClick={handleLogout}>Log out</button>
        ) : null}
      </header>

      {!auth && (
        <form className="login-card" onSubmit={handleLogin}>
          <h2>Admin Login</h2>
          <label>
            Username
            <input
              type="text"
              value={login.username}
              onChange={(event) =>
                setLogin((prev) => ({ ...prev, username: event.target.value }))
              }
            />
          </label>
          <label>
            Password
            <input
              type="password"
              value={login.password}
              onChange={(event) =>
                setLogin((prev) => ({ ...prev, password: event.target.value }))
              }
            />
          </label>
          <button type="submit">Log in</button>
        </form>
      )}

      {auth && (
        <>
          <section
            className={`form-card${highlightForm ? " form-card-highlight" : ""}`}
            ref={formRef}
          >
            <div className="form-header">
              <h2>{editingId ? "Edit Product" : "New Product"}</h2>
              {editingId && (
                <button type="button" onClick={resetForm}>
                  New
                </button>
              )}
            </div>

            <div className="grid">
              <label>
                ID (optional)
                <input
                  type="number"
                  value={form.id}
                  onChange={(event) => setForm((prev) => ({ ...prev, id: event.target.value }))}
                />
              </label>
              <label>
                Name
                <input
                  type="text"
                  value={form.name}
                  onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                />
              </label>
              <label>
                Price
                <input
                  type="number"
                  step="0.01"
                  value={form.price}
                  onChange={(event) => setForm((prev) => ({ ...prev, price: event.target.value }))}
                />
              </label>
              <label>
                Weight
                <input
                  type="number"
                  step="0.01"
                  value={form.weight}
                  onChange={(event) => setForm((prev) => ({ ...prev, weight: event.target.value }))}
                />
              </label>
              <label>
                Video URL
                <input
                  type="text"
                  value={form.videoUrl}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, videoUrl: event.target.value }))
                  }
                />
              </label>
              <label>
                Price ID
                <input
                  type="text"
                  value={form.price_id}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, price_id: event.target.value }))
                  }
                />
              </label>
            </div>

            <label className="full-width">
              Includes
              <textarea
                rows="2"
                value={form.include}
                onChange={(event) => setForm((prev) => ({ ...prev, include: event.target.value }))}
              />
            </label>
            <label className="full-width">
              Description
              <textarea
                rows="4"
                value={form.description}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, description: event.target.value }))
                }
              />
            </label>

            <div className="custom-admin-section">
              <div className="custom-admin-heading">
                <h3>Customizer Settings</h3>
                <p>Used by the Custom Yo-Yo Builder model selector and mockup canvas.</p>
              </div>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={Boolean(form.customAvailable)}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, customAvailable: event.target.checked }))
                  }
                />
                Available for custom yo-yo requests
              </label>
              <div className="grid">
                <label>
                  Mockup Base Image URL
                  <input
                    type="text"
                    value={form.mockupBaseImage || ""}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, mockupBaseImage: event.target.value }))
                    }
                  />
                </label>
                <label>
                  Center X Ratio
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={form.mockupConfig?.centerXRatio ?? 0.5}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        mockupConfig: {
                          ...(prev.mockupConfig || {}),
                          centerXRatio: event.target.value,
                        },
                      }))
                    }
                  />
                </label>
                <label>
                  Center Y Ratio
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={form.mockupConfig?.centerYRatio ?? 0.5}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        mockupConfig: {
                          ...(prev.mockupConfig || {}),
                          centerYRatio: event.target.value,
                        },
                      }))
                    }
                  />
                </label>
                <label>
                  Radius Ratio
                  <input
                    type="number"
                    step="0.01"
                    min="0.05"
                    max="0.5"
                    value={form.mockupConfig?.radiusRatio ?? 0.22}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        mockupConfig: {
                          ...(prev.mockupConfig || {}),
                          radiusRatio: event.target.value,
                        },
                      }))
                    }
                  />
                </label>
              </div>
              <label className="full-width">
                Customizer Card Description
                <textarea
                  rows="2"
                  value={form.customDescription || ""}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, customDescription: event.target.value }))
                  }
                />
              </label>
            </div>

            <div className="tag-section">
              <div className="tag-header">
                <h3>Product Tags</h3>
                <p>Used by the store sidebar filters.</p>
              </div>
              {PRODUCT_FILTERS.map((filter) => (
                <div className="tag-group" key={filter.key}>
                  <label className="full-width tag-input-label">
                    {filter.label} tags
                    <input
                      type="text"
                      value={formatTagInput(form.tags?.[filter.key])}
                      onChange={(event) => updateTagValues(filter.key, event.target.value)}
                      placeholder={`Example: ${filter.defaultOptions.join(", ")}`}
                    />
                  </label>
                  <span className="tag-suggestions-label">Quick options</span>
                  <div className="tag-options">
                    {(tagSuggestions[filter.key] || filter.defaultOptions).map((option) => (
                      <label key={option}>
                        <input
                          type="checkbox"
                          checked={getEditableTagValues(form.tags, filter.key).includes(option)}
                          onChange={() => toggleTagValue(filter.key, option)}
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="image-row">
              <div className="image-field">
                <label>
                  Main Image URL
                  <input
                    type="text"
                    value={form.imageUrl}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, imageUrl: event.target.value }))
                    }
                  />
                </label>
                <div className="button-row">
                  <input type="file" accept="image/*" onChange={handleMainUpload} />
                  <button
                    type="button"
                    onClick={() => setForm((prev) => ({ ...prev, imageUrl: "" }))}
                  >
                    Remove
                  </button>
                </div>
              </div>
              {form.imageUrl && <img src={resolveImageUrl(form.imageUrl)} alt="Main" />}
            </div>

            <div className="color-section">
              <div className="color-header">
                <h3>Colors</h3>
                <button type="button" onClick={addColor}>
                  Add Color
                </button>
              </div>
              {(form.colors || []).map((color, index) => (
                <div className="color-row" key={`color-${index}`}>
                  <input
                    type="text"
                    placeholder="Name"
                    value={color.name || ""}
                    onChange={(event) => updateColor(index, "name", event.target.value)}
                  />
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Price"
                    value={color.price || ""}
                    onChange={(event) => updateColor(index, "price", event.target.value)}
                  />
                  <input
                    type="text"
                    placeholder="Image URL"
                    value={color.imageUrl || ""}
                    onChange={(event) => updateColor(index, "imageUrl", event.target.value)}
                  />
                  <input type="file" accept="image/*" onChange={(event) => handleColorUpload(index, event)} />
                  <button type="button" onClick={() => updateColor(index, "imageUrl", "")}>
                    Remove Image
                  </button>
                  <button type="button" onClick={() => removeColor(index)}>
                    Remove Color
                  </button>
                  {color.imageUrl && <img src={resolveImageUrl(color.imageUrl)} alt={color.name || "Color"} />}
                </div>
              ))}
            </div>

            <div className="button-row">
              <button type="button" onClick={saveProduct}>
                {editingId ? "Update" : "Create"}
              </button>
              <button type="button" onClick={resetForm}>
                Clear
              </button>
            </div>
            {status && <p className="status">{status}</p>}
          </section>

          <section className="list-card">
            <h2>Products</h2>
            <div className="product-list">
              {products.map((product) => (
                <div className="product-item" key={product.id}>
                  <div>
                    <strong>{product.name}</strong>
                    <span>ID: {product.id}</span>
                  </div>
                  <div className="button-row">
                    <button type="button" onClick={() => handleEdit(product)}>
                      Edit
                    </button>
                    <button type="button" onClick={() => deleteProduct(product.id)}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default AdminProducts;
